export const name = "TEXT_ANALYZE";
export const summary = `Extracts terms (tokens) from text and converts them into a tokenized document.`;
export const reference = `
## TEXT_ANALYZE

Extracts terms (tokens) from text and converts them into a tokenized document.

\`\`\`
TEXT_ANALYZE(
  text
  [, analyzer => { 'LOG_ANALYZER' | 'NO_OP_ANALYZER' | 'PATTERN_ANALYZER' }]
  [, analyzer_options => analyzer_options_values]
)
\`\`\`

### Description

Extracts terms (tokens) from text and converts them into a tokenized document.


### Definitions

- \`text\`: \`STRING\` value that represents the input text to tokenize.
- \`analyzer\`: A named argument with a \`STRING\` value. Determines which analyzer to use to convert \`text\` into an array of terms (tokens). This can be:

- \`analyzer_options\`: A named argument with a JSON-formatted \`STRING\` value. Takes a list of text analysis rules. For more information, see Text analyzer options.


### Details

There is no guarantee on the order of the tokens produced by this function.

If no analyzer is specified, the \`LOG_ANALYZER\` analyzer is used by default.


### Return type

\`ARRAY<STRING>\`


### Examples

The following query uses the default text analyzer, \`LOG_ANALYZER\`, with the input text:



\`\`\`
SELECT TEXT_ANALYZE('I like pie, you like-pie, they like 2 PIEs.') AS results

/*--------------------------------------------------------------------------*
 | results |
 +--------------------------------------------------------------------------+
 | ['i', 'like', 'pie', 'you', 'like', 'pie', 'they', 'like', '2', 'pies'] |
 *--------------------------------------------------------------------------*/
\`\`\`

The following query uses the \`NO_OP_ANALYZER\` text analyzer with the input text:



\`\`\`
SELECT TEXT_ANALYZE(
  'I like pie, you like-pie, they like 2 PIEs.',
  analyzer=>'NO_OP_ANALYZER'
) AS results

/*-----------------------------------------------*
 | results |
 +-----------------------------------------------+
 | 'I like pie, you like-pie, they like 2 PIEs.' |
 *-----------------------------------------------*/
\`\`\`

The following query uses the \`PATTERN_ANALYZER\`text analyzer with the input text:



\`\`\`
SELECT TEXT_ANALYZE(
  'I like pie, you like-pie, they like 2 PIEs.',
  analyzer=>'PATTERN_ANALYZER'
) AS results

/*----------------------------------------------------------------*
 | results |
 +----------------------------------------------------------------+
 | ['like', 'pie', 'you', 'like', 'pie', 'they', 'like', 'pies'] |
 *----------------------------------------------------------------*/
\`\`\`

For additional examples that include analyzer options, see Text analysis.

For helpful analyzer recipes that you can use to enhance analyzer-supported queries, see Search with text analyzers.


source: https://cloud.google.com/bigquery/docs/reference/standard-sql/text-analysis-functions
`;
